import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import { LanguageService } from '../../../services/language.service';
import { PersonDataApiModel } from '../ContactInformationLookup/models/person-data.api-model';
import { SanitizerService } from '../../../services/sanitizer.service';
import type IContactInformation from '../../../models/contact-information.model';
import type { IServerValidationError } from '../validatable.interface';
import TextFieldFormGroup from '../TextFieldFormGroup';
import type IValidatable from '../validatable.interface';
import { Status } from '../../../hooks/fetch.hook';
import { emptyContactInformation } from '../../../models/contact-information.model';
import { ContactInformationLookupField } from '../ContactInformationLookup/contact-information-lookup-field.enum';
import ValidationEngine from '../../../validation/validation-engine';
import { ValidationHelper } from '../../../helpers/validation.helper';
import AntiforgeryService from '../../../services/anti-forgery.service';
import { PersonalIdentityNumberFeature } from './personal-identity-lookup-feature.enum';

interface IPersonalIdentityNumberLookupProps {
  state: { value: IContactInformation; validationInfo: ValidationInfo };
  hideRequiredAsterisk?: boolean;
  feature: PersonalIdentityNumberFeature;
  noPersonalIdentityNumberText?: string;
  lookupButtonText?: string;
  onChange?: (value: IContactInformation) => void;
  onKeyUp?: (field: ContactInformationLookupField) => void;
}

/**
 * PersonalIdentityNumberLookup
 * Displays an input and action to look up Swedish personal identity number (personnummer)
 * and populate contact details (name, address) from a backend service.
 *
 * Behavior
 * - Renders a numeric input for the personal identity number.
 * - Validates the input locally; on "Hämta adress" click, posts to the lookup API.
 * - On success, merges looked-up person/address data into the provided state via onChange.
 * - On failure, shows a user-friendly error message and resets value via emptyContactInformation.
 * - Exposes an IValidatable ref (checkValidity, handleServerValidationErrors) for parent validation flows.
 *
 * Props
 * - state: { value, validationInfo } container for current contact info and validation state.
 * - hideRequiredAsterisk?: Hide the required asterisk on the label when true.
 * - feature: Enum to indicate the context/feature using this component, for analytics or conditional logic.
 * - lookupButtonText?: Text displayed on the lookup button; defaults to the localized "Get address" text.
 * - onChange?: Emits updated contact information (immutable) after user input or successful lookup.
 * - onKeyUp?: Optional field-level signal; parents can clear validation for personalIdentityNumber when invoked.
 *
 * Remarks
 * - Uses TranslationService and LanguageService for labels and API locale.
 * - Calls POST /api/persondata/lookup?language={lang} and maps data via PersonDataApiModel.
 * - Uses TextFieldFormGroup for consistent validation UI and accessibility.
 *
 * Example
 * <PersonalIdentityNumberLookup
 *   state={{ value: emptyContactInformation, validationInfo: new ValidationInfo() }}
 *   feature={PersonalIdentityNumberFeature.Reclamation}
 *   hideRequiredAsterisk
 *   onChange={(v) => setState((s) => ({ ...s, value: v }))}
 *   onKeyUp={() => {}} // optionally clear parent validation for PIN
 * />
 */
export default forwardRef<IValidatable, IPersonalIdentityNumberLookupProps>(function PersonalIdentityNumberLookup(
  {
    state,
    feature,
    onChange,
    onKeyUp,
    hideRequiredAsterisk = false,
    noPersonalIdentityNumberText = TranslationService.get('customerserviceforms.nopersonalidentitynumbertext'),
    lookupButtonText = TranslationService.get('common.getaddress'),
  }: IPersonalIdentityNumberLookupProps,
  ref,
) {
  // === STATE ===
  const [enteredPersonalIdentityNumberState, setEnteredPersonalIdentityNumberState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [lookupStatus, setLookupStatus] = useState(Status.notperformed);
  const [lookupErrorCode, setLookupErrorCode] = useState<number | undefined>(undefined);

  // === REFERENCES ===
  const personalIdentityNumberInputRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      // Handle validation of the received personal identity number separately, this is not the same as the
      // state of the personal identity number textbox
      const personalIdentityNumberValidationInfo = new ValidationInfo(TranslationService.get('common.personalidentitynumber') + ' - ');
      ValidationEngine.for(personalIdentityNumberValidationInfo).required(state.value.personalIdentityNumber).validate();
      enteredPersonalIdentityNumberState.validationInfo = personalIdentityNumberValidationInfo;
      return personalIdentityNumberValidationInfo;
    },

    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        personalIdentityNumber: personalIdentityNumberInputRef.current.handleServerValidationErrors(errors),
      });
    },
  }));

  // === VARIABLES ===
  // We need this derived validation info since we want to display errors from the server that is related to the whole lookup component
  // as validation errors on the text field
  const derivedEnteredPersonalIdentityNumberValidationInfo = new ValidationInfo(
    '',
    enteredPersonalIdentityNumberState.validationInfo.errors.concat(state.validationInfo.children.personalIdentityNumber?.errors ?? []),
  );

  // === EVENT HANDLERS ===
  async function handleGetAddressClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();

    const [isFormValid] = ValidationHelper.checkValidity([
      { component: personalIdentityNumberInputRef.current, validationInfo: enteredPersonalIdentityNumberState.validationInfo, setState: setEnteredPersonalIdentityNumberState },
    ]);
    if (!isFormValid) {
      return;
    }

    setLookupStatus(Status.loading);
    setLookupErrorCode(undefined);
    try {
      // API request: POST /api/persondata/lookup?feature={feature}&language={lang}
      const response = await fetch(`/api/persondata/lookup?feature=${feature}&language=${LanguageService.getCurrentLanguage()}`, {
        method: 'POST',
        body: JSON.stringify(enteredPersonalIdentityNumberState.value),
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': await AntiforgeryService.getToken(),
        },
      });
      if (response.ok) {
        const result = PersonDataApiModel.mapToBusinessModel(await response.json());
        setLookupStatus(Status.success);
        onChange?.({ ...state.value, ...result });
      } else {
        setLookupStatus(Status.failed);
        setLookupErrorCode(response.status);
        onChange?.(emptyContactInformation);
        if (response.status === 400) {
          const responseBody = await response.json();
          if (!responseBody.errors) throw new Error('Missing errors');
          ValidationHelper.handleServerValidationErrors(responseBody.errors, [
            { component: personalIdentityNumberInputRef.current, validationInfo: enteredPersonalIdentityNumberState.validationInfo, setState: setEnteredPersonalIdentityNumberState },
          ]);
        }
      }
    } catch {
      setLookupErrorCode(500);
      setLookupStatus(Status.failed);
      onChange?.(emptyContactInformation);
    }
  }

  return (
    <div className={state.value.valid ? 'mb-3' : ''}>
      <TextFieldFormGroup
        ref={personalIdentityNumberInputRef}
        inputId="contact-information-personal-identity-number"
        serverValidationName="personalIdentityNumber"
        labelText={TranslationService.get('common.personalidentitynumber')}
        placeholder={TranslationService.get('common.validation.personalidentitynumberplaceholder')}
        inputMode="numeric"
        pattern="[0-9]*"
        hideRequiredAsterisk={hideRequiredAsterisk}
        type="text"
        ariaDescribedBy={lookupStatus === Status.failed ? 'contact-information-personal-identity-number-lookup-error' : undefined}
        autocomplete="off"
        state={{
          value: enteredPersonalIdentityNumberState.value,
          validationInfo: derivedEnteredPersonalIdentityNumberValidationInfo,
        }}
        onChange={(value) => {
          setEnteredPersonalIdentityNumberState((ps) => ({ ...ps, value }));
          onChange?.(emptyContactInformation);
        }}
        onKeyUp={() => {
          setEnteredPersonalIdentityNumberState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
          setLookupStatus(Status.notperformed);
          onKeyUp?.(ContactInformationLookupField.personalIdentityNumber);
        }}
      />
      {lookupStatus === Status.failed && lookupErrorCode !== 400 && (
        <span id="contact-information-personal-identity-number-lookup-error" role="alert" className="d-block invalid-feedback mb-3">
          {lookupErrorCode === 404 ? (
            <span>{TranslationService.get('common.persondatalookupnotfoundmessage')}</span>
          ) : (
            lookupErrorCode !== 400 && <span>{TranslationService.get('common.persondatalookuperrormessage')}</span>
          )}
        </span>
      )}
      <div className="mb-3">
        <button className="btn btn-secondary mb-md-0" disabled={enteredPersonalIdentityNumberState.value.length !== 12} onClick={handleGetAddressClick}>
          {lookupButtonText}
        </button>
      </div>
      <div className={`bg-gray p-3 ${!state.value.valid && !noPersonalIdentityNumberText ? 'd-none' : ''}`}>
        {!state.value.valid && <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(noPersonalIdentityNumberText) }}></div>}
        {state.value.valid && (
          <>
            <div>
              {TranslationService.get('common.spar.information')}
              <br />
              <a href={TranslationService.get('common.spar.link')} target="_blank" rel="noreferrer">
                {TranslationService.get('common.spar.linktext')}
              </a>
            </div>
            <div className="mt-2">
              <div className="mb-2">{TranslationService.get('common.persondatalookupsucceededmessage')}</div>
              <div>
                {state.value.firstName} {state.value.lastName}
              </div>
              {state.value.address && (
                <>
                  <div>{state.value.address.streetAddress}</div>
                  <div>
                    {state.value.address.postalCode} {state.value.address.city}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});
